/**
 * AI3D Fab-in-Place State Store
 * Zustand store for managing fabrication state
 */

import { create } from 'zustand';
import type {
  Asset,
  Job,
  JobStats,
  IntentProfile,
  ViewMode,
  FabricationResult,
} from './types';

interface FabStore {
  // Current asset
  currentAsset: Asset | null;
  setCurrentAsset: (asset: Asset | null) => void;

  // Current job
  currentJob: Job | null;
  setCurrentJob: (job: Job | null) => void;

  // Job stats
  currentStats: JobStats | null;
  setCurrentStats: (stats: JobStats | null) => void;

  // Fabrication result
  fabricationResult: FabricationResult | null;
  setFabricationResult: (result: FabricationResult | null) => void;

  // Selected profile
  selectedProfile: IntentProfile;
  setSelectedProfile: (profile: IntentProfile) => void;

  // View mode
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;

  // Job history
  jobHistory: Job[];
  addJobToHistory: (job: Job) => void;

  // UI state
  isPanelOpen: boolean;
  setIsPanelOpen: (open: boolean) => void;

  isProcessing: boolean;
  setIsProcessing: (processing: boolean) => void;

  // Raw model URL (for A/B comparison)
  rawModelUrl: string | null;
  setRawModelUrl: (url: string | null) => void;

  // Fabricated model URL
  fabricatedModelUrl: string | null;
  setFabricatedModelUrl: (url: string | null) => void;

  // Reset state
  reset: () => void;
}

export const useFabStore = create<FabStore>((set) => ({
  currentAsset: null,
  setCurrentAsset: (asset) => set({ currentAsset: asset }),

  currentJob: null,
  setCurrentJob: (job) => set({ currentJob: job }),

  currentStats: null,
  setCurrentStats: (stats) => set({ currentStats: stats }),

  fabricationResult: null,
  setFabricationResult: (result) => set({ fabricationResult: result }),

  selectedProfile: 'web_realtime',
  setSelectedProfile: (profile) => set({ selectedProfile: profile }),

  viewMode: 'raw',
  setViewMode: (mode) => set({ viewMode: mode }),

  jobHistory: [],
  addJobToHistory: (job) =>
    set((state) => ({ jobHistory: [job, ...state.jobHistory].slice(0, 20) })),

  isPanelOpen: true,
  setIsPanelOpen: (open) => set({ isPanelOpen: open }),

  isProcessing: false,
  setIsProcessing: (processing) => set({ isProcessing: processing }),

  rawModelUrl: null,
  setRawModelUrl: (url) => set({ rawModelUrl: url }),

  fabricatedModelUrl: null,
  setFabricatedModelUrl: (url) => set({ fabricatedModelUrl: url }),

  reset: () =>
    set({
      currentAsset: null,
      currentJob: null,
      currentStats: null,
      fabricationResult: null,
      rawModelUrl: null,
      fabricatedModelUrl: null,
      viewMode: 'raw',
      isProcessing: false,
    }),
}));
